import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { eventBus } from '@/events/eventBus';
import { recordManualPayment } from '@/domain/depositFlow';
import { onBookingConfirmed } from '@/domain/bookingConfirmationFlow';
import { matchOrCreateCustomer } from '@/domain/selfBookFlow';
import { sessionLengthForDuration, depositForDuration } from '@/domain/pricing';
import type { Booking, StudioId } from '@/domain/types';

/**
 * Walk-in gap-first flow (tv-pq6, Diagram 07). Two greenfield paths for a person
 * who walks into the shop:
 *  - a gap today that suits -> seatWalkInNow: tattoo today, NO deposit, they pay
 *    at checkout after the session.
 *  - no gap today -> bookWalkInFuture: book a future slot and take the deposit in
 *    the shop now.
 *
 * The gap discovery itself is read-only (buildGapList / buildDaySchedule) and the
 * picking UI is design-gated; these flows take an already-chosen slot. "Now" is
 * always the sim clock.
 */

function nowIso(): string {
  return useSimulationStore.getState().clockIso;
}

/** The studio the walk-in is standing in: the artist's studio, else the named
 *  customer's home studio, else the first studio on the books. */
function studioForWalkIn(artistId: string, customerId?: string): StudioId {
  const brain = useBrainStore.getState();
  const fromArtist = brain.artists[artistId]?.studioId;
  if (fromArtist) return fromArtist;
  const fromCustomer = customerId ? brain.customers[customerId]?.primaryStudioId : undefined;
  if (fromCustomer) return fromCustomer;
  return Object.keys(brain.studios)[0] as StudioId;
}

/** Contact captured at the front desk. Phone is required by the intake UI (it
 *  goes on file for the SMS thread); name + email are best-effort. */
export interface WalkInContact {
  name?: string;
  phone?: string;
  email?: string;
}

/**
 * Create-or-find the walk-in's customer record with the captured PHONE ON FILE,
 * so reminders / the waiver / a deposit link have a number to reach. Delegates
 * to the house intake dedupe (matchOrCreateCustomer): a returning walk-in whose
 * phone (or email / name) matches reuses their card instead of spawning a
 * duplicate. Shared entry point - the future-date deposit path resolves the
 * customer HERE before DepositLinkModal opens, and seatWalkInNow uses it for the
 * today path - so the mutation stays in the flow layer, not the component.
 */
export function findOrCreateWalkInCustomer(contact: WalkInContact, studioId: StudioId): string {
  return matchOrCreateCustomer({
    name: contact.name?.trim() || 'Walk-in',
    email: contact.email?.trim() ?? '',
    mobile: contact.phone?.trim() ?? '',
    studioId,
  });
}

/** Resolve an existing customer, else create-or-find one from the captured
 *  contact (phone on file). */
function resolveCustomerId(
  input: { customerId?: string } & WalkInContact,
  studioId: StudioId,
): string {
  if (input.customerId) return input.customerId;
  return findOrCreateWalkInCustomer(input, studioId);
}

export interface WalkInNowInput extends WalkInContact {
  customerId?: string;
  artistId: string;
  startIso: string;
  durationMin: number;
  /** Free-text "what they want", captured at the desk. Falls back to a generic brief. */
  designBrief?: string;
}

/**
 * Seat a walk-in now with NO deposit (Diagram 07). Creates a `confirmed` booking
 * so the floor flow can check them in / seat them and checkout collects the full
 * payment afterwards. `depositWaived` marks it as a deliberate no-deposit walk-in
 * (money down is collected at the make-sale), not an unpaid hold. No path seats a
 * booking without a deposit today - this is the greenfield one.
 */
export function seatWalkInNow(input: WalkInNowInput): Booking {
  const brain = useBrainStore.getState();
  const at = nowIso();
  const studioId = studioForWalkIn(input.artistId, input.customerId);
  const customerId = resolveCustomerId(input, studioId);
  const durationMin = Math.max(15, Math.round(input.durationMin));
  const bookingId = `bk-walkin-${customerId}-${new Date(input.startIso).getTime()}`;

  const booking: Booking = {
    id: bookingId,
    customerId,
    studioId,
    artistId: input.artistId,
    start: input.startIso,
    durationMin,
    sessionLength: sessionLengthForDuration(durationMin),
    status: 'confirmed',
    confirmedAt: at,
    // No deposit down: a walk-in pays in full at checkout (Diagram 07). Flagged so
    // a missing-deposit surface reads it as a walk-in, not an unpaid booking.
    depositWaived: true,
    designBrief: input.designBrief?.trim() || 'Walk-in - seated on the day.',
    referencePhotos: [],
    source: 'in-studio-walkin',
    createdAt: at,
  };
  brain.upsertBooking(booking);
  brain.appendActivity({
    id: `act-walkin-seat-${bookingId}`,
    type: 'booking-created',
    customerId,
    studioId,
    artistId: input.artistId,
    body: `Walk-in seated with ${input.artistId}. No deposit; pays at checkout.`,
    timestamp: at,
  });
  eventBus.publish('walkin:seated', { bookingId, artistId: input.artistId });
  return booking;
}

export interface WalkInFutureInput extends WalkInContact {
  customerId?: string;
  artistId: string;
  startIso: string;
  durationMin: number;
  method: 'eftpos' | 'cash' | 'bank-transfer';
}

export interface WalkInFutureResult {
  bookingId: string;
  depositId?: string;
}

/**
 * Book a walk-in a FUTURE slot with the deposit taken in the shop now (Diagram
 * 07, the "no gap today" branch). Creates a `confirmed` booking and takes the
 * standard deposit through the shared recordManualPayment (which mints + pays the
 * deposit and links it to the booking) - the deposit take is not reinvented. Runs
 * the confirm hook (waiver + calendar invite) like any newly-confirmed booking.
 */
export function bookWalkInFuture(input: WalkInFutureInput): WalkInFutureResult {
  const brain = useBrainStore.getState();
  const at = nowIso();
  const studioId = studioForWalkIn(input.artistId, input.customerId);
  const customerId = resolveCustomerId(input, studioId);
  const durationMin = Math.max(15, Math.round(input.durationMin));
  const bookingId = `bk-walkin-future-${customerId}-${new Date(input.startIso).getTime()}`;

  const booking: Booking = {
    id: bookingId,
    customerId,
    studioId,
    artistId: input.artistId,
    start: input.startIso,
    durationMin,
    sessionLength: sessionLengthForDuration(durationMin),
    status: 'confirmed',
    confirmedAt: at,
    designBrief: 'Walk-in - future booking, deposit taken in shop.',
    referencePhotos: [],
    source: 'in-studio-walkin',
    createdAt: at,
  };
  brain.upsertBooking(booking);

  const amountAud = depositForDuration(durationMin);
  recordManualPayment({ bookingId, amountAud, method: input.method });
  onBookingConfirmed(bookingId);

  // recordManualPayment linked a fresh deposit onto the booking; re-read the store
  // (the `brain` snapshot above is stale) to return its id.
  const depositId = useBrainStore.getState().bookings[bookingId]?.depositId;
  brain.appendActivity({
    id: `act-walkin-future-${bookingId}`,
    type: 'booking-created',
    customerId,
    studioId,
    artistId: input.artistId,
    body: `Walk-in booked a future session with ${input.artistId}; deposit taken in shop.`,
    timestamp: at,
  });
  eventBus.publish('walkin:booked', { bookingId, depositId, method: input.method });
  return { bookingId, depositId };
}
